import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  createScrapPost,
  isAllowedScrapImage,
  MAX_SCRAP_IMAGES,
  uploadScrapImages,
} from '../supabase';
import './PostScrapPage.css';

const CATEGORIES = [
  'Mixed scrap metal',
  'Copper / cable',
  'Aluminium',
  'Steel / iron',
  'White goods / appliances',
  'Vehicles / car parts',
  'Construction scrap',
  'Other',
];

const PROPERTY_TYPES = ['House', 'Flat', 'Bungalow', 'Commercial', 'Other'];
const SCRAP_PLACEMENTS = [
  'Front Garden',
  'Back Garden',
  'Driveway',
  'Garage',
  'Inside property',
  'Street / kerbside',
  'Other',
];
const AVAILABILITY = ['Anytime', 'Weekdays', 'Weekends', 'Evenings'];
const TIME_SLOTS = ['All Day', 'Morning', 'Afternoon', 'Evening'];
const PARKING_DISTANCES = ['Less than 10m', '10-25m', '25-50m', 'More than 50m', 'No parking nearby'];
const DIFFICULTIES = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4.5h2.2l1.2 3-1.6 1.2a10 10 0 0 0 5.5 5.5l1.2-1.6 3 1.2V18a1.5 1.5 0 0 1-1.5 1.5A12.5 12.5 0 0 1 6.5 7 1.5 1.5 0 0 1 8 4.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 20s5.5-4.8 5.5-9a5.5 5.5 0 1 0-11 0c0 4.2 5.5 9 5.5 9z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="11" r="2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 11.5L12 5l8 6.5V19a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 19v-7.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M10 20.5v-6h4v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function ScrapIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 16l4-8 3 5 2-3 5 6H5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 11.5L20 4l-5.5 16-3.2-6.3L4 11.5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

const INITIAL_FORM = {
  category: '',
  description: '',
  postal_address: '',
  location: '',
  contact_phone: '',
  availability: 'Anytime',
  property_type: 'House',
  scrap_placement: 'Front Garden',
  parking_available: true,
  parking_distance: 'Less than 10m',
  collection_difficulty: 'easy',
  collection_date: '',
  collection_time_slot: 'All Day',
};

export default function PostScrapPage() {
  const { user, isLoggedIn } = useAuth();
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user?.phone) return;
    setForm((prev) => ({
      ...prev,
      contact_phone: prev.contact_phone || user.phone,
    }));
  }, [user]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleFiles(fileList) {
    const next = Array.from(fileList || []).filter(isAllowedScrapImage);
    setFiles((prev) => [...prev, ...next].slice(0, MAX_SCRAP_IMAGES));
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    if (!user?.id) {
      setError('Please log in before posting scrap.');
      return;
    }

    setSubmitting(true);

    try {
      const imageUrls = await uploadScrapImages(files);

      const payload = {
        user_id: user.id,
        category: form.category,
        description: form.description.trim(),
        postal_address: form.postal_address.trim(),
        location: form.location.trim(),
        contact_phone: form.contact_phone.trim(),
        availability: form.availability,
        image_urls: imageUrls,
        property_type: form.property_type,
        scrap_placement: form.scrap_placement,
        parking_available: Boolean(form.parking_available),
        parking_distance: form.parking_distance,
        collection_difficulty: form.collection_difficulty,
        collection_date: form.collection_date || null,
        collection_time_slot: form.collection_time_slot,
        status: 'active',
        reference_number: `SS${Date.now().toString().slice(-8)}`,
      };

      let created;
      try {
        created = await createScrapPost(payload);
      } catch (insertError) {
        // If DB trigger owns reference numbers, retry without our value.
        const message = String(insertError?.message || '');
        if (!/reference_number/i.test(message)) throw insertError;
        const { reference_number, ...withoutRef } = payload;
        created = await createScrapPost(withoutRef);
      }
      setResult(created);
      setForm({
        ...INITIAL_FORM,
        contact_phone: user.phone || '',
      });
      setFiles([]);
    } catch (err) {
      setError(err?.message || 'Could not post scrap. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="post-scrap-page">
      <section className="post-scrap-hero" aria-labelledby="post-scrap-heading">
        <div className="post-scrap-hero-inner">
          <h1 id="post-scrap-heading">Post Scrap</h1>
          <p>
            Tell us what scrap you have and where it is. Collectors can pick it up — free to post,
            no obligation.
          </p>
        </div>
      </section>

      <section className="post-scrap-body" aria-label="Post scrap form">
        <div className="post-scrap-inner">
          {!isLoggedIn ? (
            <div className="post-scrap-success post-scrap-gate">
              <h2>Log in to post scrap</h2>
              <p>
                You need a SnapScrap account before you can create a listing. Sign up or log in to
                continue.
              </p>
              <div className="post-scrap-success-actions">
                <Link className="post-scrap-submit" to="/login" state={{ from: '/post-scrap' }}>
                  Log in
                </Link>
                <Link className="post-scrap-secondary-link" to="/signup" state={{ from: '/post-scrap' }}>
                  Create an account
                </Link>
              </div>
            </div>
          ) : result ? (
            <div className="post-scrap-success">
              <h2>Scrap posted successfully</h2>
              <p>
                Thanks — your scrap listing has been submitted
                {result.reference_number ? (
                  <>
                    {' '}
                    with reference <strong>{result.reference_number}</strong>
                  </>
                ) : null}
                . A SnapScrap collector will review the details and get in touch shortly.
              </p>
              <div className="post-scrap-success-actions">
                <button
                  type="button"
                  className="post-scrap-submit"
                  onClick={() => setResult(null)}
                >
                  Post another load
                </button>
                <Link className="post-scrap-secondary-link" to="/">
                  Back to home
                </Link>
              </div>
            </div>
          ) : (
            <form className="post-scrap-form" onSubmit={handleSubmit}>
              <div className="post-scrap-row">
                <label className="post-scrap-field">
                  <span>
                    Phone <em>*</em>
                  </span>
                  <span className="post-scrap-input">
                    <PhoneIcon />
                    <input
                      name="contact_phone"
                      type="tel"
                      placeholder="Your phone number"
                      required
                      value={form.contact_phone}
                      onChange={(e) => updateField('contact_phone', e.target.value)}
                    />
                  </span>
                </label>

                <label className="post-scrap-field">
                  <span>
                    Postcode <em>*</em>
                  </span>
                  <span className="post-scrap-input">
                    <PinIcon />
                    <input
                      name="postal_address"
                      type="text"
                      placeholder="e.g. LS1 1AA"
                      required
                      value={form.postal_address}
                      onChange={(e) => updateField('postal_address', e.target.value)}
                    />
                  </span>
                </label>
              </div>

              <label className="post-scrap-field">
                <span>
                  Home address <em>*</em>
                </span>
                <span className="post-scrap-input">
                  <HomeIcon />
                  <input
                    name="location"
                    type="text"
                    placeholder="Full collection address"
                    required
                    value={form.location}
                    onChange={(e) => updateField('location', e.target.value)}
                  />
                </span>
              </label>

              <div className="post-scrap-row">
                <label className="post-scrap-field">
                  <span>
                    Scrap category <em>*</em>
                  </span>
                  <span className="post-scrap-input post-scrap-select">
                    <ScrapIcon />
                    <select
                      name="category"
                      required
                      value={form.category}
                      onChange={(e) => updateField('category', e.target.value)}
                    >
                      <option value="" disabled>
                        Select scrap type...
                      </option>
                      {CATEGORIES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>

                <label className="post-scrap-field">
                  <span>Property type</span>
                  <span className="post-scrap-input post-scrap-select">
                    <HomeIcon />
                    <select
                      name="property_type"
                      value={form.property_type}
                      onChange={(e) => updateField('property_type', e.target.value)}
                    >
                      {PROPERTY_TYPES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              </div>

              <div className="post-scrap-row">
                <label className="post-scrap-field">
                  <span>Scrap placement</span>
                  <span className="post-scrap-input post-scrap-select">
                    <select
                      name="scrap_placement"
                      value={form.scrap_placement}
                      onChange={(e) => updateField('scrap_placement', e.target.value)}
                    >
                      {SCRAP_PLACEMENTS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>

                <label className="post-scrap-field">
                  <span>Collection difficulty</span>
                  <span className="post-scrap-input post-scrap-select">
                    <select
                      name="collection_difficulty"
                      value={form.collection_difficulty}
                      onChange={(e) => updateField('collection_difficulty', e.target.value)}
                    >
                      {DIFFICULTIES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              </div>

              <div className="post-scrap-row">
                <label className="post-scrap-field">
                  <span>Availability</span>
                  <span className="post-scrap-input post-scrap-select">
                    <select
                      name="availability"
                      value={form.availability}
                      onChange={(e) => updateField('availability', e.target.value)}
                    >
                      {AVAILABILITY.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>

                <label className="post-scrap-field">
                  <span>Time slot</span>
                  <span className="post-scrap-input post-scrap-select">
                    <select
                      name="collection_time_slot"
                      value={form.collection_time_slot}
                      onChange={(e) => updateField('collection_time_slot', e.target.value)}
                    >
                      {TIME_SLOTS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              </div>

              <div className="post-scrap-row">
                <label className="post-scrap-field">
                  <span>Preferred collection date</span>
                  <span className="post-scrap-input">
                    <CalendarIcon />
                    <input
                      name="collection_date"
                      type="date"
                      value={form.collection_date}
                      onChange={(e) => updateField('collection_date', e.target.value)}
                    />
                  </span>
                </label>

                <label className="post-scrap-field">
                  <span>Parking distance</span>
                  <span className="post-scrap-input post-scrap-select">
                    <select
                      name="parking_distance"
                      value={form.parking_distance}
                      onChange={(e) => updateField('parking_distance', e.target.value)}
                    >
                      {PARKING_DISTANCES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              </div>

              <label className="post-scrap-field">
                <span>
                  Describe your scrap <em>*</em>
                </span>
                <textarea
                  name="description"
                  placeholder="e.g. Copper pipe offcuts, old radiator, mixed steel from a garage clear-out..."
                  maxLength={1000}
                  rows={5}
                  required
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                />
                <small>{form.description.length} / 1000 characters</small>
              </label>

              <div className="post-scrap-field">
                <span>Photos of scrap (optional, max {MAX_SCRAP_IMAGES})</span>
                <div
                  className={`post-scrap-dropzone ${dragOver ? 'is-over' : ''}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragOver(false);
                    handleFiles(event.dataTransfer.files);
                  }}
                >
                  <p>
                    Drag &amp; Drop photos or{' '}
                    <button type="button" onClick={() => fileInputRef.current?.click()}>
                      Browse
                    </button>
                  </p>
                  <small>JPEG, PNG, WebP, GIF, HEIC · max 5 MB each</small>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic"
                    multiple
                    hidden
                    onChange={(event) => handleFiles(event.target.files)}
                  />
                </div>
                {files.length > 0 && (
                  <ul className="post-scrap-files">
                    {files.map((file, index) => (
                      <li key={`${file.name}-${file.lastModified}-${index}`}>
                        <span>{file.name}</span>
                        <button type="button" onClick={() => removeFile(index)}>
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <label className="post-scrap-check">
                <input
                  type="checkbox"
                  name="parking_available"
                  checked={form.parking_available}
                  onChange={(e) => updateField('parking_available', e.target.checked)}
                />
                <span>Parking is available near the scrap</span>
              </label>

              {error ? (
                <p className="post-scrap-error" role="alert">
                  {error}
                </p>
              ) : null}

              <button type="submit" className="post-scrap-submit" disabled={submitting}>
                {submitting ? 'Posting…' : 'Post Scrap'}
                {!submitting ? <SendIcon /> : null}
              </button>
            </form>
          )}

          <aside className="post-scrap-aside">
            <div className="post-scrap-tip">
              <h2>Tips for a faster pickup</h2>
              <ul>
                <li>Add clear photos of the scrap pile</li>
                <li>Separate metals if you can</li>
                <li>Include access notes for collectors</li>
                <li>Share an accurate postcode and address</li>
              </ul>
            </div>
            <div className="post-scrap-tip">
              <h2>Want to collect scrap?</h2>
              <p>Join SnapScrap as a licensed collector and get matched with nearby posts.</p>
              <Link className="post-scrap-aside-btn" to="/become-a-collector">
                Become a Collector →
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
